import type { ChangeEvent } from "react";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import type { BackgroundJobKind, BackgroundJobStatus, BackgroundJobTrigger } from "@/api/background-job/_models/BackgroundJob";
import { MultiSelectFilter } from "@/components/MultiSelectFilter";
import { ClearFiltersButton } from "@/components/ClearFiltersButton";
import { SidePanel } from "@/components/SidePanel";
import { JobStatusChip } from "@/features/job/JobStatusChip";

/** The Jobs page's filter values, sent to GET /background-jobs (?kind=&status=&trigger=). */
export interface JobListFilterValues {
  /** One kind, or "" for every kind. */
  kind: string;
  /** Empty means every status. */
  statuses: BackgroundJobStatus[];
  /** Who started it, or "" for both. */
  trigger: BackgroundJobTrigger | "";
}

/** No filter: every job. */
export const JOB_LIST_EMPTY_FILTER: JobListFilterValues = {
  kind: "",
  statuses: [],
  trigger: "",
};

/** Whether any filter narrows the list. */
export const isJobListFilterActive = (filter: JobListFilterValues): boolean => {
  return filter.kind !== "" || filter.statuses.length > 0 || filter.trigger !== "";
};

const STATUSES: BackgroundJobStatus[] = ["QUEUED", "RUNNING", "DONE", "FAILED"];

interface JobListFilterPanelProps {
  filter: JobListFilterValues;
  isOpen: boolean;
  kinds: BackgroundJobKind[];
  onChange: (filter: JobListFilterValues) => void;
  onClose: () => void;
}

/** The Jobs page's filters in a side panel: kind, statuses and who started it. */
export const JobListFilterPanel = (props: JobListFilterPanelProps) => {
  const { filter, isOpen, kinds, onChange, onClose } = props;

  const handleKindChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filter, kind: event.target.value });
  };

  const handleStatusesChange = (statuses: BackgroundJobStatus[]) => {
    onChange({ ...filter, statuses });
  };

  const handleTriggerChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...filter, trigger: event.target.value as BackgroundJobTrigger | "" });
  };

  const handleClear = () => {
    onChange(JOB_LIST_EMPTY_FILTER);
  };

  const renderStatus = (status: BackgroundJobStatus) => {
    return <JobStatusChip status={status} />;
  };

  return (
    <SidePanel
      isOpen={isOpen}
      onClose={onClose}
      title="Filter jobs"
    >
      <Stack
        spacing={2}
        sx={{
          flex: 1,
          mt: 0.5,
        }}
        useFlexGap
      >
        <TextField
          label="Job"
          onChange={handleKindChange}
          select
          size="small"
          value={filter.kind}
        >
          <MenuItem value="">All jobs</MenuItem>
          {kinds.map((kind) => (
            <MenuItem
              key={kind.kind}
              value={kind.kind}
            >
              {kind.label}
            </MenuItem>
          ))}
        </TextField>
        <MultiSelectFilter
          allLabel="All statuses"
          onChange={handleStatusesChange}
          options={STATUSES}
          renderOption={renderStatus}
          value={filter.statuses}
        />
        <TextField
          label="Started by"
          onChange={handleTriggerChange}
          select
          size="small"
          value={filter.trigger}
        >
          <MenuItem value="">Anyone</MenuItem>
          <MenuItem value="MANUAL">Someone (manual)</MenuItem>
          <MenuItem value="SCHEDULE">A schedule</MenuItem>
        </TextField>
        <ClearFiltersButton
          isActive={isJobListFilterActive(filter)}
          onClick={handleClear}
        />
      </Stack>
    </SidePanel>
  );
};
